import {IAttributeInfo, IAttributeName, IOperation, IRule, IType} from './Rule';

export type SchemaInfo = IAttributeInfo[];

export class Schema {
    constructor(private schema: SchemaInfo) {
    }

    static buildRule(attr: IAttributeInfo): IRule {
        return {attr: attr.name, value: null, op: null, children: [], variable: false};
    }

    attribute(name: IAttributeName): IAttributeInfo {
        return this.schema.find(attribute => attribute.name === name);
    }

    attributes(): IAttributeInfo[] {
        return this.schema;
    }

    defaultValue(attribute: IAttributeInfo, op: IOperation = IOperation.Eq) {
        if ([IOperation.In, IOperation.NotIn].includes(op)) {
            return [];
        } else {
            switch (attribute.type) {
                case IType.Numeric:
                    return 0.0;
                case IType.String:
                    return '';
                case IType.Bool:
                    return true;
            }
        }
    }

    castToArray(attribute: IAttributeInfo, value: any): any {
        switch (attribute.type) {
            case IType.Bool:
                return [value];
            case IType.Numeric:
                return value === 0 ? [] : [value];
            case IType.String:
                return value.split(',').filter(part => part && part.trim().length !== 0);
        }
    }

    castToSingle(attribute: IAttributeInfo, value: any): any {
        if (Array.isArray(value)) {
            switch (attribute.type) {
                case IType.Bool:
                    return value.length > 0 ? value[0] : this.defaultValue(attribute);
                case IType.Numeric:
                    return value.length > 0 ? parseFloat(value[0]) : this.defaultValue(attribute);
                case IType.String:
                    return value.length > 0 ? value.join(',') : this.defaultValue(attribute);
            }
        }
    }

    castRule(rule: IRule, op: IOperation): IRule {
        let attribute = this.attribute(rule.attr);
        if (rule.value == null) {
            rule.value = this.defaultValue(attribute);
        }

        if ([IOperation.In, IOperation.NotIn].includes(op)) {
            if (!Array.isArray(rule.value)) {
                rule.value = this.castToArray(attribute, rule.value);
            }
        } else {
            if (Array.isArray(rule.value)) {
                rule.value = this.castToSingle(attribute, rule.value);
            }
        }
        rule.op = op;
        return rule;
    }

    concatRules(rules: IRule[], rule: IRule): IRule[] {
        return rules.concat(rule).sort(((a, b) => this.attribute(a.attr).priority - this.attribute(b.attr).priority));
    }

    allRulesDeclared(rules: IRule[]): boolean {
        let result = true;
        rules.forEach(rule => {
            let present = (!!rule.op && !!rule.attr);
            if (!present) {
                result = false;
            } else {
                result = this.allRulesDeclared(rule.children || []);
            }
        });
        return result;
    }
}
